import { useEffect, useState, useCallback } from 'react';
import { modelsAPI } from '../api/models';
import { Link } from 'react-router-dom';
import { Eye, Check, Filter, Sparkles, Search, Zap } from 'lucide-react';
import Loader from '../components/common/Loader';
import SeoMeta from '../components/SeoMeta';

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Galerie de modèles Dev-Tech",
  description: "Modèles web modernes, e-commerce, CRM et business prêts à être personnalisés.",
  url: "https://devtechpro.fr/models",
  mainEntity: {
    "@type": "ItemList",
    itemListElement: []
  }
};

// Initial categories configuration
const allCategories = [
  { id: 'all', label: 'Tous les modèles', icon: '✨', color: 'from-purple-500 to-pink-500' },
  { id: 'website', label: 'Sites Web', icon: '🌐', color: 'from-blue-500 to-cyan-500' },
  { id: 'ecommerce', label: 'E-commerce', icon: '🛒', color: 'from-green-500 to-emerald-500' },
  { id: 'crm', label: 'CRM & Gestion', icon: '📊', color: 'from-indigo-500 to-blue-500' },
  { id: 'portfolio', label: 'Portfolio', icon: '🎨', color: 'from-pink-500 to-rose-500' },
  { id: 'business', label: 'Business', icon: '💼', color: 'from-yellow-500 to-orange-500' },
  { id: 'blog', label: 'Blogs', icon: '📝', color: 'from-teal-500 to-cyan-500' }
];

export default function Models() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableCategories, setAvailableCategories] = useState(allCategories);

  const loadModels = useCallback(async () => {
    try {
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      const response = await modelsAPI.getAll(category);
      const modelsData = response.data;
      setModels(modelsData);

      // Extract unique categories from models
      const uniqueCategories = [...new Set(modelsData.map(model => model.category))];
      
      // Filter categories to only show those that have models
      const filteredCategories = allCategories.filter(cat => 
        cat.id === 'all' || uniqueCategories.includes(cat.id)
      );
      
      setAvailableCategories(filteredCategories);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const filteredModels = models.filter(model =>
    model.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    model.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900">
      <SeoMeta
        title="Galerie de modèles web"
        description="Parcourez les modèles Dev-Tech : sites vitrines, e-commerce, CRM et portfolios prêts à personnaliser.
        "
        canonical="https://devtechpro.fr/models"
        openGraph={{
          title: 'Modèles Dev-Tech',
          description: 'Galerie de modèles web, e-commerce et business prêts à personnaliser.',
          url: 'https://devtechpro.fr/models'
        }}
        jsonLd={jsonLd}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/10 to-pink-900/20" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-purple-300">NOS RÉALISATIONS</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Galerie de <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Modèles</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
              Découvrez nos réalisations et inspirez-vous pour votre futur projet. 
              Chaque modèle peut être entièrement personnalisé selon vos besoins.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher un modèle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Models Section */}
      <section className="pb-32">
        <div className="container mx-auto px-4">
          {/* Filters */}
          <div className="mb-12 p-6 bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <Filter className="w-5 h-5 text-purple-400" />
              <span className="text-lg font-semibold text-gray-300">Filtrer par catégorie:</span>
              <span className="text-sm text-gray-500">
                ({availableCategories.length - 1} catégorie{availableCategories.length - 1 > 1 ? 's' : ''} disponible{availableCategories.length - 1 > 1 ? 's' : ''})
              </span>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {availableCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`group relative px-5 py-3 rounded-xl transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25'
                      : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2 font-medium">
                    <span>{category.icon}</span>
                    {category.label}
                    {category.id !== 'all' && (
                      <span className="text-xs bg-gray-700/50 px-2 py-0.5 rounded-full">
                        {models.filter(m => m.category === category.id).length}
                      </span>
                    )}
                  </span>
                  
                  {selectedCategory === category.id && (
                    <div className="absolute inset-0 rounded-xl border-2 border-purple-400/50" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Models Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModels.map((model) => (
              <div key={model._id} className="group relative">
                {/* Card Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 group-hover:border-purple-500/50 transition-colors duration-300" />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                
                {/* Card Content */}
                <Link 
                  to={`/models/${model._id}`}
                  className="relative h-full overflow-hidden rounded-2xl block"
                >
                  {/* Preview Image */}
                  <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                    {model.previewImage ? (
                      <img 
                        src={model.previewImage} 
                        alt={model.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        <div className="text-4xl opacity-30">
                          {model.category === 'ecommerce' ? '🛒' : 
                           model.category === 'portfolio' ? '🎨' : 
                           model.category === 'blog' ? '📝' : '🌐'}
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                        <div className="flex gap-3">
                          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white text-purple-600 rounded-lg font-semibold">
                            <Eye className="w-4 h-4" />
                            Voir détails
                          </div>
                          
                          {model.demoUrl && (
                            <a
                              href={model.demoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors duration-200"
                            >
                              <Zap className="w-4 h-4" />
                              Démo
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors duration-300">
                        {model.title}
                      </h3>
                      <div className="px-3 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-purple-500/30">
                        <span className="text-lg font-bold text-white">à partir: {model.price.base}€</span>
                      </div>
                    </div>

                    <p className="text-gray-400 mb-4 line-clamp-2">
                      {model.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2 mb-4">
                      {model.features?.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-sm text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-800">
                      <span className="px-3 py-1.5 bg-gray-800/50 rounded-lg text-sm text-gray-400 border border-gray-700">
                        {model.category}
                      </span>
                      <div className="text-sm font-semibold text-purple-400 group-hover:text-purple-300 transition-colors duration-200 flex items-center gap-1">
                        Plus de détails
                        <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Glow Effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 rounded-2xl" />
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredModels.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 flex items-center justify-center">
                <Search className="w-12 h-12 text-gray-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Aucun modèle trouvé
              </h3>
              <p className="text-gray-400 max-w-md mx-auto">
                Aucun modèle ne correspond à votre recherche. Essayez de modifier vos critères.
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col items-center gap-6 p-8 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-gray-700 max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  Besoin d'un modèle sur mesure ?
                </h3>
                <p className="text-gray-300 mb-6">
                  Nous pouvons créer un site web unique spécifiquement adapté à vos besoins
                </p>
              </div>
              
              <Link
                to="/contact"
                className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-200"
              >
                Demander un devis personnalisé
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}